import React from "react";
import { AlertOctagon, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * The result of the server's import data check (core.services.import_quality):
 * every column judged by what is in it, before anything is saved.
 *
 * Red problems stop the import until they are fixed (for example a name column
 * that is all numbers: how "Owner ID" became 100 landlords' names, 27 Oct 2026).
 * Amber ones are shown so they are a choice, not a surprise.
 */
export default function DataCheckPanel({ quality, compact = false }) {
  if (!quality) return null;
  const issues = quality.issues || [];
  if (!issues.length) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
        <CheckCircle2 size={14} /> Data check passed: names, emails and phone numbers look right.
      </div>
    );
  }
  const blocks = issues.filter((i) => i.severity === "block");
  const warns = issues.filter((i) => i.severity !== "block");
  return (
    <div className="space-y-2" data-testid="data-check">
      {blocks.length > 0 && (
        <IssueList
          tone="block" items={blocks} compact={compact}
          title={blocks.length === 1 ? "Fix this before importing" : `Fix these ${blocks.length} problems before importing`}
        />
      )}
      {warns.length > 0 && (
        <IssueList tone="warn" items={warns} compact={compact} title="Check these" />
      )}
    </div>
  );
}

function IssueList({ tone, title, items, compact }) {
  const block = tone === "block";
  const Icon = block ? AlertOctagon : AlertTriangle;
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${block ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-[#8a5a00]"}`}>
      <div className="flex items-center gap-1.5 font-bold mb-1">
        <Icon size={14} /> {title}
      </div>
      <ul className="space-y-1">
        {items.map((i, idx) => (
          <li key={`${i.code}-${i.column}-${idx}`}>
            {i.message}
            {!compact && i.examples?.length > 0 && (
              <span className="block opacity-80">e.g. {i.examples.join(", ")}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
